/**
 * @file Recalls route-local state.
 */

import { appData } from "../../app/state/state.js";

const DEFAULT_RECALLS_ROUTE_STATE = Object.freeze({
    listenerStatus: "idle",
    listenerError: null,
    latestKnownMonthBoundary: null,
    oldestKnownMonthBoundary: null,
    loadedThroughMonth: null,
    loadedMonthKeys: [],
    monthLoadingState: {},
    monthLoadErrors: {},
    monthRowsByKey: {},
    monthScaffold: [],
    expandedYearHeadings: [],
    collapsedYearHeadings: [],
    expandedMonths: [],
    collapsedMonths: [],
    draftsCollapsed: false,
    advancedSearch: {
        active: false,
        cap: null,
        criteria: null,
        error: null,
        reachedCap: false,
        results: [],
        status: "idle",
        total: 0,
    },
    optimisticRecallDrafts: [],
    operationalRecalls: [],
    openControlMenu: null,
    openFilterKey: null,
    searchDraft: "",
    showClosed: false,
    showPreviousMonths: false,
    filters: {
        processed: [],
        provider: [],
        reason: [],
        search: "",
        status: [],
    },
    exportSelection: {
        active: false,
        selectedIds: [],
    },
    sort: {
        field: "status",
        direction: "asc",
    },
});

/**
 * Mutable UI/runtime state for the standalone Recalls route.
 * @type {object}
 */
export const recallsRouteState = {
    ...DEFAULT_RECALLS_ROUTE_STATE,
};

/**
 * Ensures the global app data cache has Recalls buckets.
 * @returns {void}
 */
export function ensureRecallsDataBuckets() {
    if (!Array.isArray(appData.recalls)) {
        appData.recalls = [];
    }
    if (!Array.isArray(appData.recallDrafts)) {
        appData.recallDrafts = [];
    }
    if (!Array.isArray(appData.closedRecalls)) {
        appData.closedRecalls = [];
    }
}

/**
 * Replaces cached active Recalls rows.
 * @param {Array<object>} recalls Active recall rows from Firestore.
 * @returns {void}
 */
export function setCachedRecalls(recalls) {
    ensureRecallsDataBuckets();
    appData.recalls.splice(
        0,
        appData.recalls.length,
        ...(Array.isArray(recalls) ? recalls : []),
    );
}

function mergeRowsById(rowSets) {
    const rows = new Map();
    rowSets.flat().forEach((row) => {
        if (row?.id) {
            rows.set(row.id, row);
        }
    });
    return Array.from(rows.values());
}

function getCachedMonthRows({ closed = false } = {}) {
    return Object.values(recallsRouteState.monthRowsByKey || {})
        .flatMap((cache) => closed ? (cache.closedRows || []) : (cache.activeRows || []));
}

function getRouteMonthKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

function addRouteMonths(monthKey, offset) {
    const [year, month] = normalizeMonthKey(monthKey) ?
        monthKey.split("-").map(Number) :
        [new Date().getFullYear(), new Date().getMonth() + 1];
    return getRouteMonthKey(new Date(year, month - 1 + offset, 1));
}

function getOperationalUpperMonthKey() {
    return addRouteMonths(getRouteMonthKey(), 1);
}

function getRecallEffectiveMonth(row = {}) {
    if (normalizeMonthKey(row.effectiveMonth)) {
        return row.effectiveMonth;
    }
    const dateValue = row.recall_date || "";
    return /^\d{4}-\d{2}/.test(dateValue) ? dateValue.slice(0, 7) : "";
}

function isOperationalActiveMonthRow(row = {}) {
    const monthKey = getRecallEffectiveMonth(row);
    return Boolean(monthKey && monthKey <= getOperationalUpperMonthKey());
}

function getCachedActiveRowsForMerge() {
    const activeRows = getCachedMonthRows();
    if (recallsRouteState.listenerStatus !== "ready") {
        return activeRows;
    }
    return activeRows.filter((row) => !isOperationalActiveMonthRow(row));
}

function refreshRecallsRouteCaches() {
    setCachedRecalls(mergeRowsById([
        getCachedActiveRowsForMerge(),
        recallsRouteState.operationalRecalls || [],
    ]));
    setCachedClosedRecalls(recallsRouteState.showClosed === true ?
        mergeRowsById([getCachedMonthRows({ closed: true })]) :
        []);
}

function removeRowsById(rows = [], recallId) {
    return (Array.isArray(rows) ? rows : [])
        .filter((row) => row?.id !== recallId);
}

function upsertRowById(rows = [], row = {}) {
    if (!row?.id) {return Array.isArray(rows) ? rows : [];}
    const nextRows = removeRowsById(rows, row.id);
    nextRows.push(row);
    return nextRows;
}

/**
 * Finds a Recall row across the current route caches and loaded app data.
 * @param {string} recallId Recall document id.
 * @returns {object|null} Matching Recall row, if loaded.
 */
export function findCachedRecall(recallId) {
    return [
        ...(recallsRouteState.operationalRecalls || []),
        ...getCachedMonthRows(),
        ...getCachedMonthRows({ closed: true }),
        ...(recallsRouteState.advancedSearch?.results || []),
        ...(appData.recalls || []),
        ...(appData.closedRecalls || []),
    ].find((row) => row?.id === recallId) || null;
}

function getStatusClosedRole(statusId) {
    const mappings = appData.config?.recalls?.statusRoleMappings || {};
    const role = Object.entries(mappings)
        .find(([, mappedStatusId]) => mappedStatusId === statusId)?.[0] || "";
    return ["booked", "rescheduled", "declined", "cancelled"].includes(role) ?
        role :
        "";
}

function buildStatusMutatedRecall(row = {}, statusId) {
    const closedRole = getStatusClosedRole(statusId);
    const isClosedResolved = Boolean(closedRole);
    const effectiveMonth = getRecallEffectiveMonth(row);
    return {
        ...row,
        status: statusId,
        isClosedResolved,
        closedRole: closedRole || null,
        closedMonth: isClosedResolved ? (row.closedMonth || effectiveMonth || null) : null,
        exportEligible: isClosedResolved ? false : row.exportEligible,
    };
}

function updateAdvancedSearchRecall(row) {
    if (recallsRouteState.advancedSearch?.active !== true || !row?.id) {return;}
    recallsRouteState.advancedSearch = {
        ...recallsRouteState.advancedSearch,
        results: (recallsRouteState.advancedSearch.results || [])
            .map((result) => result?.id === row.id ? { ...result, ...row } : result),
    };
}

function updateMonthCacheRecall(row) {
    const effectiveMonth = getRecallEffectiveMonth(row);
    const closedMonth = row.closedMonth || effectiveMonth;
    const nextCache = {};
    Object.entries(recallsRouteState.monthRowsByKey || {}).forEach(
        ([monthKey, cache]) => {
            nextCache[monthKey] = {
                ...cache,
                activeRows: removeRowsById(cache.activeRows, row.id),
                closedRows: removeRowsById(cache.closedRows, row.id),
            };
        },
    );

    if (row.isClosedResolved === true && closedMonth) {
        const cache = nextCache[closedMonth] || {};
        if (recallsRouteState.showClosed === true || cache.closedLoaded === true) {
            nextCache[closedMonth] = {
                ...cache,
                closedLoaded: true,
                closedRows: upsertRowById(cache.closedRows, row),
            };
        }
    } else if (effectiveMonth) {
        const cache = nextCache[effectiveMonth] || {};
        if (cache.activeLoaded === true) {
            nextCache[effectiveMonth] = {
                ...cache,
                activeLoaded: true,
                activeRows: upsertRowById(cache.activeRows, row),
            };
        }
    }

    recallsRouteState.monthRowsByKey = nextCache;
}

/**
 * Applies a confirmed status mutation to every loaded Recalls route cache.
 * @param {string} recallId Recall id.
 * @param {string} statusId Confirmed status id.
 * @returns {object|null} Updated cached row, if the row was loaded.
 */
export function applyRecallStatusMutationToRouteCaches(recallId, statusId) {
    if (!recallId || !statusId) {return null;}
    const existing = findCachedRecall(recallId);
    if (!existing) {return null;}
    const row = buildStatusMutatedRecall(existing, statusId);
    if (row.isClosedResolved === true) {
        recallsRouteState.operationalRecalls = removeRowsById(
            recallsRouteState.operationalRecalls,
            recallId,
        );
    } else if (isOperationalActiveMonthRow(row)) {
        recallsRouteState.operationalRecalls = upsertRowById(
            recallsRouteState.operationalRecalls,
            row,
        );
    }
    updateMonthCacheRecall(row);
    updateAdvancedSearchRecall(row);
    refreshRecallsRouteCaches();
    return row;
}

/**
 * Applies confirmed detail edits to every loaded Recalls route cache.
 * @param {string} recallId Recall id.
 * @param {object} data Confirmed detail-edit payload.
 * @returns {object|null} Updated cached row, if the row was loaded.
 */
export function applyRecallDetailsMutationToRouteCaches(recallId, data = {}) {
    if (!recallId) {return null;}
    const existing = findCachedRecall(recallId);
    if (!existing) {return null;}
    const recallDate = data.recall_date || existing.recall_date || null;
    const effectiveMonth = getRecallEffectiveMonth({
        effectiveMonth: data.effectiveMonth,
        recall_date: recallDate,
    });
    const row = {
        ...existing,
        ...data,
        closedMonth: existing.isClosedResolved === true ?
            (data.closedMonth || existing.closedMonth || effectiveMonth || null) :
            null,
        effectiveMonth,
        recall_date: recallDate,
        updatedAt: null,
    };

    recallsRouteState.operationalRecalls = removeRowsById(
        recallsRouteState.operationalRecalls,
        recallId,
    );
    if (row.isClosedResolved !== true && isOperationalActiveMonthRow(row)) {
        recallsRouteState.operationalRecalls = upsertRowById(
            recallsRouteState.operationalRecalls,
            row,
        );
    }
    updateMonthCacheRecall(row);
    updateAdvancedSearchRecall(row);
    refreshRecallsRouteCaches();
    return row;
}

/**
 * Replaces the default operational active Recalls cache.
 * @param {Array<object>} recalls Operational active rows from Firestore.
 * @returns {void}
 */
export function setCachedOperationalRecalls(recalls) {
    recallsRouteState.operationalRecalls =
        Array.isArray(recalls) ? recalls : [];
    refreshRecallsRouteCaches();
}

/**
 * Replaces cached shared Recalls drafts.
 * @param {Array<object>} drafts Draft rows from Firestore.
 * @returns {void}
 */
export function setCachedRecallDrafts(drafts) {
    ensureRecallsDataBuckets();
    const serverDrafts = Array.isArray(drafts) ? drafts : [];
    const serverIds = new Set(serverDrafts
        .map((draft) => draft?.id)
        .filter(Boolean));
    recallsRouteState.optimisticRecallDrafts =
        (recallsRouteState.optimisticRecallDrafts || [])
            .filter((draft) => draft?.id && !serverIds.has(draft.id));
    const optimisticDrafts = recallsRouteState.optimisticRecallDrafts
        .filter((draft) => draft?.id && !serverIds.has(draft.id));
    appData.recallDrafts.splice(
        0,
        appData.recallDrafts.length,
        ...optimisticDrafts,
        ...serverDrafts,
    );
}

/**
 * Inserts or replaces a cached Recall draft row for immediate UI feedback.
 * @param {object} draft Draft row.
 * @returns {void}
 */
export function upsertCachedRecallDraft(draft = {}) {
    if (!draft.id) {return;}
    ensureRecallsDataBuckets();
    const index = appData.recallDrafts
        .findIndex((entry) => entry?.id === draft.id);
    if (index >= 0) {
        appData.recallDrafts.splice(index, 1, {
            ...appData.recallDrafts[index],
            ...draft,
        });
        return;
    }
    appData.recallDrafts.unshift(draft);
}

/**
 * Adds a locally pending Recall draft row.
 * @param {object} draft Draft row.
 * @returns {void}
 */
export function addOptimisticRecallDraft(draft = {}) {
    if (!draft.id) {return;}
    recallsRouteState.optimisticRecallDrafts = [
        draft,
        ...(recallsRouteState.optimisticRecallDrafts || [])
            .filter((entry) => entry?.id !== draft.id),
    ];
    upsertCachedRecallDraft(draft);
}

/**
 * Replaces a pending Recall draft id after the backend returns the real id.
 * @param {string} previousId Temporary draft id.
 * @param {object} draft Confirmed draft row.
 * @returns {void}
 */
export function replaceOptimisticRecallDraft(previousId, draft = {}) {
    if (!previousId || !draft.id) {return;}
    recallsRouteState.optimisticRecallDrafts =
        (recallsRouteState.optimisticRecallDrafts || [])
            .filter((entry) => entry?.id !== previousId);
    removeCachedRecallDraft(previousId);
    addOptimisticRecallDraft(draft);
}

/**
 * Removes a Recall draft from pending and visible caches.
 * @param {string} draftId Draft id.
 * @returns {void}
 */
export function removeCachedRecallDraft(draftId) {
    if (!draftId) {return;}
    recallsRouteState.optimisticRecallDrafts =
        (recallsRouteState.optimisticRecallDrafts || [])
            .filter((entry) => entry?.id !== draftId);
    ensureRecallsDataBuckets();
    const index = appData.recallDrafts
        .findIndex((entry) => entry?.id === draftId);
    if (index >= 0) {
        appData.recallDrafts.splice(index, 1);
    }
}

/**
 * Replaces cached closed Recalls rows.
 * @param {Array<object>} recalls Closed recall rows from Firestore.
 * @returns {void}
 */
export function setCachedClosedRecalls(recalls) {
    ensureRecallsDataBuckets();
    appData.closedRecalls.splice(
        0,
        appData.closedRecalls.length,
        ...(Array.isArray(recalls) ? recalls : []),
    );
}

function normalizeMonthKey(monthKey) {
    return /^\d{4}-\d{2}$/.test(monthKey || "") ? monthKey : "";
}

/**
 * Stores a known oldest/latest Recalls month boundary.
 * @param {"latest"|"oldest"} boundary Boundary name.
 * @param {string|null} monthKey Month key in YYYY-MM form.
 * @returns {void}
 */
export function setRecallsMonthBoundary(boundary, monthKey) {
    const normalizedMonthKey = normalizeMonthKey(monthKey);
    if (boundary === "latest") {
        recallsRouteState.latestKnownMonthBoundary = normalizedMonthKey || null;
    }
    if (boundary === "oldest") {
        recallsRouteState.oldestKnownMonthBoundary = normalizedMonthKey || null;
    }
}

/**
 * Returns whether a month bucket is already loaded for the route session.
 * @param {string} monthKey Month key in YYYY-MM form.
 * @param {object} [options] Check options.
 * @param {boolean} [options.includeActive=true] Whether active rows are needed.
 * @param {boolean} [options.includeClosed=false] Whether closed rows are needed.
 * @returns {boolean} True when the requested month data is cached.
 */
export function hasLoadedRecallsMonth(
    monthKey,
    { includeActive = true, includeClosed = false } = {},
) {
    const normalizedMonthKey = normalizeMonthKey(monthKey);
    if (!normalizedMonthKey) {return false;}
    const cache = recallsRouteState.monthRowsByKey?.[normalizedMonthKey];
    if (!cache) {return false;}
    if (includeActive === true && cache.activeLoaded !== true) {return false;}
    if (includeClosed === true && cache.closedLoaded !== true) {return false;}
    return true;
}

/**
 * Tracks month fetch loading or error state.
 * @param {string} monthKey Month key in YYYY-MM form.
 * @param {object} [options] Loading options.
 * @param {boolean} [options.loading=false] Whether the month is loading.
 * @param {Error|null} [options.error=null] Loading error, if any.
 * @returns {void}
 */
export function setRecallsMonthLoadState(
    monthKey,
    { error = null, loading = false } = {},
) {
    const normalizedMonthKey = normalizeMonthKey(monthKey);
    if (!normalizedMonthKey) {return;}
    recallsRouteState.monthLoadingState = {
        ...(recallsRouteState.monthLoadingState || {}),
        [normalizedMonthKey]: loading === true,
    };
    recallsRouteState.monthLoadErrors = {
        ...(recallsRouteState.monthLoadErrors || {}),
        [normalizedMonthKey]: error || null,
    };
}

/**
 * Stores fetched month rows in the route-session cache.
 * @param {string} monthKey Month key in YYYY-MM form.
 * @param {object} rows Fetched rows.
 * @param {Array<object>} [rows.activeRows=[]] Active rows.
 * @param {Array<object>} [rows.closedRows=[]] Closed rows.
 * @param {object} [options] Cache options.
 * @param {boolean} [options.activeLoaded=true] Whether active rows were fetched.
 * @param {boolean} [options.closedLoaded=false] Whether closed rows were fetched.
 * @returns {void}
 */
export function setRecallsMonthRows(
    monthKey,
    { activeRows = [], closedRows = [] } = {},
    { activeLoaded = true, closedLoaded = false } = {},
) {
    const normalizedMonthKey = normalizeMonthKey(monthKey);
    if (!normalizedMonthKey) {return;}
    const currentCache =
        recallsRouteState.monthRowsByKey?.[normalizedMonthKey] || {};
    recallsRouteState.monthRowsByKey = {
        ...(recallsRouteState.monthRowsByKey || {}),
        [normalizedMonthKey]: {
            activeLoaded:
                currentCache.activeLoaded === true || activeLoaded === true,
            activeRows: activeLoaded === true && Array.isArray(activeRows) ?
                activeRows :
                (currentCache.activeRows || []),
            closedLoaded:
                currentCache.closedLoaded === true || closedLoaded === true,
            closedRows: closedLoaded === true ?
                (Array.isArray(closedRows) ? closedRows : []) :
                (currentCache.closedRows || []),
        },
    };
    recallsRouteState.loadedMonthKeys = Array.from(new Set([
        ...(recallsRouteState.loadedMonthKeys || []),
        normalizedMonthKey,
    ])).sort();
    setRecallsMonthLoadState(normalizedMonthKey);
    addRecallsMonthScaffold(normalizedMonthKey);
    refreshRecallsRouteCaches();
}

/**
 * Clears route-session closed Recalls caches.
 * @returns {void}
 */
export function clearRecallsClosedRouteCache() {
    const nextCache = {};
    Object.entries(recallsRouteState.monthRowsByKey || {}).forEach(
        ([monthKey, cache]) => {
            nextCache[monthKey] = {
                ...cache,
                closedLoaded: false,
                closedRows: [],
            };
        },
    );
    recallsRouteState.monthRowsByKey = nextCache;
    setCachedClosedRecalls([]);
}

function normalizeRecallsFilterValues(value) {
    const values = Array.isArray(value) ? value : [value];
    return [...new Set(values
        .map((entry) => String(entry || "").trim())
        .filter((entry) => entry && entry !== "all"))];
}

/**
 * Updates a single Recalls filter.
 * @param {string} key Filter key.
 * @param {string|Array<string>} value Filter value.
 * @returns {void}
 */
export function setRecallsFilter(key, value) {
    if (!Object.hasOwn(recallsRouteState.filters, key)) {return;}
    recallsRouteState.filters[key] = key === "search" ?
        String(value || "") :
        normalizeRecallsFilterValues(value);
}

/**
 * Clears filter controls while preserving sort and loaded-listener state.
 * @returns {void}
 */
export function clearRecallsFilters() {
    recallsRouteState.filters = structuredClone(
        DEFAULT_RECALLS_ROUTE_STATE.filters,
    );
    recallsRouteState.openFilterKey = null;
    recallsRouteState.searchDraft = "";
}

/**
 * Updates Recalls sorting.
 * @param {string} field Sort field.
 * @param {string} direction Sort direction.
 * @returns {void}
 */
export function setRecallsSort(field, direction = "asc") {
    recallsRouteState.sort = {
        field: field || "status",
        direction: direction === "desc" ? "desc" : "asc",
    };
}

/**
 * Stores successful advanced-search results as a route-local snapshot.
 * @param {object} params Search result params.
 * @param {number|null} [params.cap=null] Result cap.
 * @param {object} [params.criteria={}] Criteria used for the search.
 * @param {boolean} [params.reachedCap=false] Whether the search reached its cap.
 * @param {Array<object>} [params.rows=[]] Result rows.
 * @param {number|null} [params.total=null] Result total.
 * @returns {void}
 */
export function setRecallsAdvancedSearchResults({
    cap = null,
    criteria = {},
    reachedCap = false,
    rows = [],
    total = null,
} = {}) {
    recallsRouteState.advancedSearch = {
        active: true,
        cap,
        criteria: structuredClone(criteria || {}),
        error: null,
        reachedCap: reachedCap === true,
        results: Array.isArray(rows) ? rows : [],
        status: "ready",
        total: Number.isFinite(Number(total)) ? Number(total) :
            (Array.isArray(rows) ? rows.length : 0),
    };
    clearRecallsExportSelection();
}

/**
 * Stores the latest advanced-search criteria and error without clearing rows.
 * @param {object} params Error params.
 * @param {object} [params.criteria={}] Criteria used for the failed search.
 * @param {string|null} [params.error=null] Error message.
 * @returns {void}
 */
export function setRecallsAdvancedSearchError({
    criteria = {},
    error = null,
} = {}) {
    recallsRouteState.advancedSearch = {
        ...(recallsRouteState.advancedSearch || {}),
        criteria: structuredClone(criteria || {}),
        error: error || "Advanced search could not be completed.",
        status: "error",
    };
}

/**
 * Clears advanced-search result mode while preserving normal route caches.
 * @returns {void}
 */
export function clearRecallsAdvancedSearch() {
    recallsRouteState.advancedSearch = structuredClone(
        DEFAULT_RECALLS_ROUTE_STATE.advancedSearch,
    );
    clearRecallsExportSelection();
}

/**
 * Tracks the currently open Recalls control menu.
 * @param {string|null} menuId Menu id.
 * @returns {void}
 */
export function setRecallsOpenControlMenu(menuId) {
    recallsRouteState.openControlMenu = menuId || null;
    if (menuId !== "filter") {
        recallsRouteState.openFilterKey = null;
    }
}

/**
 * Tracks the active Recalls filter-menu detail page.
 * @param {string|null} filterKey Filter key.
 * @returns {void}
 */
export function setRecallsOpenFilterKey(filterKey) {
    recallsRouteState.openControlMenu = "filter";
    recallsRouteState.openFilterKey = filterKey || null;
}

/**
 * Toggles the Show Closed route state.
 * @param {boolean} shouldShow Whether closed rows should be shown.
 * @returns {void}
 */
export function setRecallsShowClosed(shouldShow) {
    recallsRouteState.showClosed = shouldShow === true;
    if (!recallsRouteState.showClosed) {
        recallsRouteState.showPreviousMonths = false;
    }
}

/**
 * Toggles prior-month Recalls containers while closed rows are visible.
 * @param {boolean} shouldShow Whether previous month containers should show.
 * @returns {void}
 */
export function setRecallsShowPreviousMonths(shouldShow) {
    recallsRouteState.showPreviousMonths =
        recallsRouteState.showClosed === true && shouldShow === true;
}

/**
 * Adds a month to the route-local visible scaffold list.
 * @param {string} monthKey Month key in YYYY-MM form.
 * @returns {void}
 */
export function addRecallsMonthScaffold(monthKey) {
    const normalizedMonthKey = normalizeMonthKey(monthKey);
    if (!normalizedMonthKey) {return;}
    const months = new Set(recallsRouteState.monthScaffold || []);
    months.add(normalizedMonthKey);
    recallsRouteState.monthScaffold = Array.from(months).sort();
}

/**
 * Expands a month section without toggling it.
 * @param {string} monthKey Month key in YYYY-MM form.
 * @returns {void}
 */
export function expandRecallsMonth(monthKey) {
    const normalizedMonthKey = normalizeMonthKey(monthKey);
    if (!normalizedMonthKey) {return;}
    const expanded = new Set(recallsRouteState.expandedMonths || []);
    const collapsed = new Set(recallsRouteState.collapsedMonths || []);
    expanded.add(normalizedMonthKey);
    collapsed.delete(normalizedMonthKey);
    recallsRouteState.expandedMonths = Array.from(expanded).sort();
    recallsRouteState.collapsedMonths = Array.from(collapsed).sort();
}

/**
 * Stores the loaded-through effective month.
 * @param {string} monthKey Month key in YYYY-MM form.
 * @param {object} [options] Loaded-through options.
 * @param {boolean} [options.expand=false] Whether to expand the requested month.
 * @returns {void}
 */
export function setRecallsLoadedThroughMonth(monthKey, { expand = false } = {}) {
    const normalizedMonthKey = normalizeMonthKey(monthKey);
    if (!normalizedMonthKey) {return;}
    recallsRouteState.loadedThroughMonth = normalizedMonthKey;
    addRecallsMonthScaffold(normalizedMonthKey);
    if (expand === true) {
        expandRecallsMonth(normalizedMonthKey);
    }
}

/**
 * Toggles a month section expansion state.
 * @param {string} monthKey Month key.
 * @param {object} [options] Toggle options.
 * @param {boolean|null} [options.currentlyExpanded=null] Rendered expansion state.
 * @returns {void}
 */
export function toggleRecallsMonthExpansion(
    monthKey,
    { currentlyExpanded = null } = {},
) {
    const normalizedMonthKey = normalizeMonthKey(monthKey);
    if (!normalizedMonthKey) {return;}
    addRecallsMonthScaffold(normalizedMonthKey);
    const expanded = new Set(recallsRouteState.expandedMonths || []);
    const collapsed = new Set(recallsRouteState.collapsedMonths || []);
    const isExpanded = currentlyExpanded === null ?
        expanded.has(normalizedMonthKey) :
        currentlyExpanded === true;
    if (isExpanded) {
        expanded.delete(normalizedMonthKey);
        collapsed.add(normalizedMonthKey);
    } else {
        collapsed.delete(normalizedMonthKey);
        expanded.add(normalizedMonthKey);
    }
    recallsRouteState.expandedMonths = Array.from(expanded).sort();
    recallsRouteState.collapsedMonths = Array.from(collapsed).sort();
}

/**
 * Toggles a Recalls year heading expansion state.
 * @param {string|number} year Year heading.
 * @param {object} [options] Toggle options.
 * @param {boolean|null} [options.currentlyExpanded=null] Rendered expansion state.
 * @returns {void}
 */
export function toggleRecallsYearHeading(
    year,
    { currentlyExpanded = null } = {},
) {
    const yearKey = String(year || "").trim();
    if (!/^\d{4}$/.test(yearKey)) {return;}
    const expanded = new Set(recallsRouteState.expandedYearHeadings || []);
    const collapsed = new Set(recallsRouteState.collapsedYearHeadings || []);
    const isExpanded = currentlyExpanded === null ?
        !collapsed.has(yearKey) :
        currentlyExpanded === true;
    if (isExpanded) {
        expanded.delete(yearKey);
        collapsed.add(yearKey);
    } else {
        collapsed.delete(yearKey);
        expanded.add(yearKey);
    }
    recallsRouteState.expandedYearHeadings = Array.from(expanded).sort();
    recallsRouteState.collapsedYearHeadings = Array.from(collapsed).sort();
}

/**
 * Expands a year heading without toggling it.
 * @param {string|number} year Year heading.
 * @returns {void}
 */
export function expandRecallsYearHeading(year) {
    const yearKey = String(year || "").trim();
    if (!/^\d{4}$/.test(yearKey)) {return;}
    const expanded = new Set(recallsRouteState.expandedYearHeadings || []);
    const collapsed = new Set(recallsRouteState.collapsedYearHeadings || []);
    expanded.add(yearKey);
    collapsed.delete(yearKey);
    recallsRouteState.expandedYearHeadings = Array.from(expanded).sort();
    recallsRouteState.collapsedYearHeadings = Array.from(collapsed).sort();
}

/**
 * Toggles the shared Recall drafts section expansion state.
 * @param {object} [options] Toggle options.
 * @param {boolean|null} [options.currentlyExpanded=null] Rendered expansion state.
 * @returns {void}
 */
export function toggleRecallsDraftsExpansion({ currentlyExpanded = null } = {}) {
    const isExpanded = currentlyExpanded === null ?
        recallsRouteState.draftsCollapsed !== true :
        currentlyExpanded === true;
    recallsRouteState.draftsCollapsed = isExpanded;
}

/**
 * Enables or disables export-selection mode.
 * @param {boolean} active Whether export-selection mode is active.
 * @returns {void}
 */
export function setRecallsExportSelectionMode(active) {
    recallsRouteState.exportSelection = {
        active: active === true,
        selectedIds: [],
    };
    if (active === true) {
        clearRecallsFilters();
    }
}

/**
 * Clears selected export row ids while preserving export mode.
 * @returns {void}
 */
export function clearRecallsExportSelection() {
    recallsRouteState.exportSelection = {
        active: recallsRouteState.exportSelection?.active === true,
        selectedIds: [],
    };
}

/**
 * Returns selected export row ids.
 * @returns {Array<string>} Selected Recall ids.
 */
export function getSelectedRecallsExportIds() {
    return Array.isArray(recallsRouteState.exportSelection?.selectedIds) ?
        [...recallsRouteState.exportSelection.selectedIds] :
        [];
}

/**
 * Toggles a selected export row id.
 * @param {string} recallId Recall id.
 * @param {boolean} selected Whether the row should be selected.
 * @returns {void}
 */
export function setRecallsExportRowSelected(recallId, selected) {
    if (!recallId) {return;}
    const selectedIds = new Set(getSelectedRecallsExportIds());
    if (selected === true) {
        selectedIds.add(recallId);
    } else {
        selectedIds.delete(recallId);
    }
    recallsRouteState.exportSelection = {
        active: recallsRouteState.exportSelection?.active === true,
        selectedIds: Array.from(selectedIds),
    };
}

/**
 * Toggles selected export row ids for a month section.
 * @param {Array<string>} recallIds Selectable Recall ids.
 * @param {boolean} selected Whether ids should be selected.
 * @returns {void}
 */
export function setRecallsExportMonthSelected(recallIds = [], selected) {
    const selectedIds = new Set(getSelectedRecallsExportIds());
    recallIds.filter(Boolean).forEach((recallId) => {
        if (selected === true) {
            selectedIds.add(recallId);
        } else {
            selectedIds.delete(recallId);
        }
    });
    recallsRouteState.exportSelection = {
        active: recallsRouteState.exportSelection?.active === true,
        selectedIds: Array.from(selectedIds),
    };
}

/**
 * Keeps export selection limited to currently visible selectable rows.
 * @param {Array<string>} allowedIds Visible selectable Recall ids.
 * @returns {void}
 */
export function restrictRecallsExportSelection(allowedIds = []) {
    const allowed = new Set(allowedIds.filter(Boolean));
    const selectedIds = getSelectedRecallsExportIds()
        .filter((recallId) => allowed.has(recallId));
    recallsRouteState.exportSelection = {
        active: recallsRouteState.exportSelection?.active === true,
        selectedIds,
    };
}

/**
 * Resets route-local state and cached Recalls rows.
 * @returns {void}
 */
export function resetRecallsRouteState() {
    Object.assign(recallsRouteState, structuredClone(DEFAULT_RECALLS_ROUTE_STATE));
    setCachedOperationalRecalls([]);
    setCachedRecallDrafts([]);
    setCachedClosedRecalls([]);
}
